import Vue from 'vue';
import { CancelToken } from 'axios';
import WebFont from 'webfontloader';
import {
  ref,
  computed,
  inject,
  watch,
  provide,
  onBeforeUnmount,
} from '@vue/composition-api';

import {
  cloneDeep,
  pick,
  get,
  set,
  omit,
} from 'lodash';
import {
  UtcDate,
  isDateBeforeTodayUtc,
} from '@/helpers/date';
import {
  deepRemoveKeys,
  wait,
  createAbtastyScript,
  removeAbtastyScript,
} from '@/helpers/common';
import useDrawer from '@/composables/useDrawer';

import useInternalTrackers from '@/composables/useInternalTrackers';
import { ADDITIONAL_COVER_OPTIONS } from '../constants/products/travel';

const MODAL_ALERTS = {
  quoteExpired: 'quoteExpired',
  expiredQuestions: 'expiredQuestions',
  noResults: 'noResults',
  exceedQuotes: 'exceedQuotes',
};

const TOTAL_WAIT_MS = 2500;

// TODO: [BE issue]
// Temporary fix: skip if no optionalExtra received in quote response (PU-5541)
// Check quote details for fields that must be received
function isValidQuoteDetails (qd, productName) {
  if (!qd.optionalExtra && productName !== 'breakdown') return false;

  return true;
}

function adjustMissingData (data, productName) {
  const mappedData = [];
  let counter = 0;

  data.forEach((quote) => {
    quote.details.forEach((detail) => {
      if (!isValidQuoteDetails(detail, productName)) return;
      counter++;
      mappedData.push({
        ...quote,
        isVisible: true,
        details: [{
          ...detail,
          calcAnnual: 0,
          calcDeposit: 0,
          calcMonthly: 0,
          calcMonthlyTotal: 0,
          calcVariedAmount: 0,
        }],
        shown: true,
        id: counter,
      });
    });
  });

  return mappedData;
}

const prepareJourney = (payload, productName, newDateOfInsuranceStart) => {
  let insuranceDataKey;
  let expiredQuoteNewModalDataKey;

  const isExpiredQuoteNewModal = !!newDateOfInsuranceStart;

  switch (productName) {
    case 'home':
      insuranceDataKey = 'insured[0].dateOfInsuranceStart';
      expiredQuoteNewModalDataKey = 'insured[0].expiredQuoteNewModal';
      break;
    case 'pet':
      insuranceDataKey = 'proposer.dateOfInsuranceStart';
      expiredQuoteNewModalDataKey = 'proposer.expiredQuoteNewModal';
      break;
    case 'travel':
      insuranceDataKey = 'trip.startDate';
      expiredQuoteNewModalDataKey = 'trip.expiredQuoteNewModal';
      break;
    default:
      insuranceDataKey = 'cover.dateOfInsuranceStart';
      expiredQuoteNewModalDataKey = 'cover.expiredQuoteNewModal';
  }

  // Remove any keys related to expired quote (used on car and travel for now)
  const data = omit(cloneDeep(payload), insuranceDataKey + 'Expired');

  // date of insurance start
  const dateOfInsuranceStart = get(data, insuranceDataKey);

  if (dateOfInsuranceStart === null || isDateBeforeTodayUtc(dateOfInsuranceStart)) {
    set(data, insuranceDataKey, newDateOfInsuranceStart ?? new UtcDate().format());
    if (isExpiredQuoteNewModal) set(data, expiredQuoteNewModalDataKey, true);
  }

  return data;
};

export default ({
  detailsDescription,
  stepIds,
  gtmTracking,
  travelDropdowns,
}, {
  root,
  refs,
}) => {
  const internalTrackers = useInternalTrackers(root);
  provide('internalTrackers', internalTrackers);

  const config = inject('config');
  const constants = inject('constants');
  const productName = inject('productName');
  const apiProductName = inject('apiProductName');
  const { abtastyCampaigns } = inject('abTastyControllerUI');

  // composables
  const {
    isDetailsDrawerOpen,
    isOptionsDrawerVisible,
    drawerComponent,
    drawerDetails,
    openDetailsDrawer,
    closeDetailsDrawer,
    drawerWidth,
  } = useDrawer(root, refs);

  // request data
  const sid = ref(null);
  const request = ref(null);
  const quoteRef = ref(null);
  // results
  const formData = ref(null);
  const allResults = ref([]);
  const brokerResults = ref([]);
  const resultsPageLayout = ref(null);
  const resultsPageDisplay = ref(null);
  const failedResults = ref([]);
  const questionSetChanged = ref(false);
  // mapped form data to display to user
  const quoteSummary = ref(null);
  // quoting progress
  const isQuotesLoading = ref(false);
  const hasSucceededQuotes = ref(false);
  const quotingDonePercentage = ref(0);
  const isUpdateQuoteShow = ref(false);
  // displaying quote details in right sided panel
  const detailsQuote = ref(null);
  // left side panel
  const isViewMonthly = ref(false); // payment type
  // filters for displayed results
  const optionalExtras = ref([]);
  const policyTypes = ref([]);
  const telematics = ref(['black box', 'app based', 'plug and drive', 'pay per mile']);
  const restSelectedFilters = ref({});
  // quiting results failed modals
  const isNoResults = ref(false);
  const modalAlert = ref(false);
  // position of quote on page
  const quotePositionNumber = ref(0);
  // results requote modal
  const isResultsRequoteModalShown = ref(false);
  const isResultsRequoteShown = ref(false);
  const resultsRequoteRedirectUrl = ref('');

  // Autoquote
  const isAutoquteModalShown = ref(false);
  // COMPUTED START HERE
  const routeSid = computed(() => root.$route.params.sid);
  const getInitDetailsDescription = computed(() => detailsDescription);
  const getDetailsDescription = computed(() => detailsDescription[drawerDetails.value]);
  const brokerShownResults = computed(() => { return filterShownResults(brokerResults); });
  const shownResults = computed(() => { return filterShownResults(allResults); });
  const quoteVehicleDetailsMake = computed(() => get(quoteSummary.value, ['Vehicle Details', 'Vehicle data', 'make']) ?? get(quoteSummary.value, ['Vehicle Details', 'Base vehicle details', 'make']));
  const quoteCoverDetails = computed(() => get(quoteSummary.value, ['Cover Details', 'Cover type']));
  const hasUrlQueryActionForShowDetails = computed(() => root.$route.query?.rp_action === 'showdetails');
  const quoteVehicleDetailsUse = computed(() => get(quoteSummary.value, ['Vehicle Details', 'Vehicle use']) ?? get(quoteSummary.value, ['Cover Details', 'Vehicle use']) ?? get(quoteSummary.value, ['Vehicle Details', 'Motorhome used for']));
  const quotesQuantity = computed(() => shownResults.value.filter(q => q.isVisible).length);
  // get selected entity to be able to get appropriate label according to the selected value
  const selectedOptionalExtras = computed(() => {
    if (productName === 'travel') {
      const selectedTravelExtras = ADDITIONAL_COVER_OPTIONS.filter(item => {
        return optionalExtras.value.find(selectedFilter => {
          return item.value === selectedFilter;
        });
      });

      if (selectedTravelExtras?.length) return selectedTravelExtras;
    }

    return constants.OPTIONAL_EXTRAS_OPTIONS.filter(item => {
      return optionalExtras.value.find(selectedFilter => item.value === selectedFilter);
    });
  });
  const insuredName = computed(() => {
    if (['car', 'van', 'bike', 'motorhome'].includes(productName)) {
      return formData?.value.drivers?.[0].firstName;
    }

    if (productName === 'travel') {
      return formData?.value?.travellers?.firstName;
    }

    if (productName === 'pet') {
      return formData?.value?.proposer?.firstName;
    }

    if (productName === 'home') {
      return formData?.value?.insured?.[0]?.firstName;
    }

    // for Breakdown, Bicycle products
    return formData.value?.yourDetails.firstName;
  });
  const updateUsersViewMonthly = (val) => {
    switch (productName) {
      case 'home':
        formData.value.insured[0].propertyPaymentType = val ? 'M' : 'A';
        break;
      default:
        formData.value.cover.payInsuranceMonthly = val;
    }
  };

  const toggleAutoquoteModal = (isShown) => {
    isAutoquteModalShown.value = isShown;
  };

  const setIsMonthly = () => {
    switch (productName) {
      case 'home':
        return formData?.value?.insured[0]?.propertyPaymentType === 'M';
      case 'pet':
        return restSelectedFilters.value?.isViewMonthly || false;
      case 'bicycle':
      case 'breakdown':
      case 'travel':
        return false;
      default:
        return formData?.value?.cover?.payInsuranceMonthly || false;
    }
  };
  const maxExcess = ref('any');

  /*
  *  QUOTES
  */
  const getQuotes = async (isInitialCall) => {
    isQuotesLoading.value = true;

    if (!sid.value || sid.value !== routeSid.value) {
      sid.value = routeSid.value;
    }

    if (!request.value) {
      request.value = CancelToken.source();
    }

    if (!formData.value) {
      const response = await loadJourney();

      if (response?.questionSetChanged) {
        isQuotesLoading.value = false;
        questionSetChanged.value = true;
        modalAlert.value = MODAL_ALERTS.expiredQuestions;
        return;
      }
    }

    isViewMonthly.value = setIsMonthly();

    const { data } = config.enableBrokerSolution
      ? await root.$api.getBrokerSolutionResults({
        sid: routeSid.value,
        productType: productName,
        partner: config.partner,
      })
      : await root.$api.getQuoteResults({
        sid: routeSid.value,
        returnAll: true,
        cancelToken: request.value.token,
      });

    const isQuotingDone = data.quotingDone || false;
    const results = data.results || [];
    const brokerQuotes = data.brokerResults || [];
    resultsPageDisplay.value = data.resultsPageDisplay || null;
    resultsPageLayout.value = data.resultsPageLayout || null;
    const succeededQuotes = results.filter(({ status }) => status === 'SUCCESS');
    const failedQuotes = results.filter(({ status }) => status !== 'SUCCESS');
    hasSucceededQuotes.value = config.enableBrokerSolution
      ? !!succeededQuotes.length
      : !!(succeededQuotes.length || brokerQuotes.length);

    quoteRef.value = data.quoteRef || null;
    quotingDonePercentage.value = data.quotingDonePercentage || 0;

    if (isQuotingDone) {
      if (!quoteSummary.value) {
        await getQuoteSummary();
        await processCookies();
      }
      // [SU-5472] - expanding condition with product check, because we are getting results immediately on breakdown
      // and default logic doesn't work for it
      if (isInitialCall || config.enableBrokerSolution || productName === 'breakdown') {
        await gtmTracking('results');
      }

      await gtmTracking('results-done');
      createAbtastyScript(config);

      allResults.value = adjustMissingData(succeededQuotes, productName);
      allResults.value = updatePricing(allResults);

      if (config.enableBrokerSolution) {
        brokerResults.value = adjustMissingData(brokerQuotes, productName);
        brokerResults.value = updatePricing(brokerResults);
      }

      failedResults.value = failedQuotes;

      isQuotesLoading.value = false;

      if (!succeededQuotes.length) {
        modalAlert.value = MODAL_ALERTS.noResults;
        isNoResults.value = true;
      }

      resultsRequoteRedirectUrl.value = data?.redirectUrl || '';
      isResultsRequoteShown.value = Boolean(resultsRequoteRedirectUrl.value && isQuotingDone);

      return;
    }

    // Quoting not done + No results at all => expired quote
    if (!isQuotingDone && !results.length) {
      if (!formData.value.quoteEngineQuote) {
        if (request.value) {
          await request.value.cancel();
          request.value = null;
        }
        modalAlert.value = MODAL_ALERTS.expiredQuestions;
        return;
      }
    }

    // Quoting not done + we received new SUCCESS results
    if (!isQuotingDone && succeededQuotes.length) {
      allResults.value = adjustMissingData(succeededQuotes, productName);
      allResults.value = updatePricing(allResults);
      if (config.enableBrokerSolution) {
        brokerResults.value = adjustMissingData(brokerQuotes, productName);
        brokerResults.value = updatePricing(brokerResults);
      }

      if (!quoteSummary.value) {
        await getQuoteSummary();
        await processCookies();

        // fetch and run tracking for quotes loading. Prevent second call for brokerSolution
        // [SU-5472] - skip breakdown, because we have a gtm call for it on quoting one
        if (!config.enableBrokerSolution || productName !== 'breakdown') {
          await gtmTracking('results');
        }
      }
    }

    await wait(TOTAL_WAIT_MS);
    if (sid.value === routeSid.value) {
      await getQuotes();
    }
  };

  const refreshQuote = async () => {
    if (!formData.value) {
      alert('NO FORM DATA AVAILABLE');
      return;
    }

    try {
      const { data: journey } = await root.$api.showJourney({
        productName: apiProductName,
        productPartnerId: config.partner,
      });

      deepRemoveKeys(pick(journey, stepIds), null, key => key.indexOf('_') === 0);

      if (productName === 'travel') updateTravelExtras(journey?.trip);

      const response = await root.$api.getQuote(
        apiProductName,
        journey,
        config.partner,
      );
      const { status, data } = response;
      if (status === 'success') {
        allResults.value = [];
        quoteSummary.value = null;

        await root.$router.push({
          name: `${productName}-insurance-results`,
          params: { sid: data.SID },
        }, () => {});

        await getQuotes();
      }
    } catch (error) {
      if (error.response.data.errors === 'Too many requests') {
        if (config.partnerQuoteLimitAction === 'redirect') {
          // or change window location
          window.location = config.partnerQuoteLimitData;
          return;
        }
        // show quote limit dialog and prevent further progress + reset captcha
        modalAlert.value = MODAL_ALERTS.exceedQuotes;
        return;
      }
      modalAlert.value = MODAL_ALERTS.expiredQuestions;
      console.dir(error);
    }
  };

  const getQuoteSummary = async () => {
    const summary = await root.$api.getQuoteSummary(routeSid.value, apiProductName);

    quoteSummary.value = summary.data.quoteSummaryDetails;
  };

  const reQuote = async (newDateOfInsuranceStart) => {
    isQuotesLoading.value = true;
    await reset();
    isUpdateQuoteShow.value = false;

    let initialViewMonthly;

    switch (productName) {
      case 'home':
        initialViewMonthly = formData.value.insured[0]?.propertyPaymentType === 'M';
        break;
      default:
        initialViewMonthly = formData.value.cover?.payInsuranceMonthly;
    }

    const isViewMonthlyChanged = typeof initialViewMonthly === 'boolean' && isViewMonthly.value !== initialViewMonthly;

    if (isViewMonthlyChanged) {
      updateUsersViewMonthly(isViewMonthly.value);
    }

    let insuranceDataKey;

    switch (productName) {
      case 'home':
        insuranceDataKey = 'insured';
        break;
      case 'travel':
        insuranceDataKey = 'trip';
        break;
      default:
        insuranceDataKey = 'cover';
    }

    // Reset internal trackers counters
    internalTrackers.resetClicksToBuyOnlineAmount();
    internalTrackers.resetClicksCallToProviderAmount();

    await Vue.nextTick();

    formData.value = prepareJourney(formData.value, productName, newDateOfInsuranceStart);

    await saveJourney(insuranceDataKey);
    await refreshQuote();
  };

  const cancelReQuote = prevVal => {
    switch (productName) {
      case 'home':
        root.$set(formData.value.insured, 0, {
          ...formData.value.insured[0],
          ...prevVal,
        });
        break;
      case 'motorhome':

        formData.value.cover = {
          ...formData.value.cover,
          ...prevVal.cover,
        };

        formData.value.vehicle = {
          ...formData.value.vehicle,
          ...prevVal.vehicle,
        };
        break;
      default:
        formData.value.cover = {
          ...formData.value.cover,
          ...prevVal,
        };
    }

    isUpdateQuoteShow.value = false;
  };

  const toggleQuoteShown = (id, results = allResults.value) => {
    const quote = results.find(result => result.id === id) || null;
    if (!quote) return;
    quote.shown = !quote.shown;
  };

  /*
  * JOURNEY
  */
  const loadJourney = async () => {
    try {
      const { status, data } = await root.$api.showJourney({
        productName: apiProductName,
        productPartnerId: config.partner,
      });

      if (status === 'success') {
        formData.value = cloneDeep(data);

        if (productName === 'travel') updateTravelExtras(data?.trip);

        let insuranceDataKey;

        switch (productName) {
          case 'home':
            insuranceDataKey = 'insured[0].dateOfInsuranceStart';
            break;
          case 'travel':
            insuranceDataKey = data?.trip?.startDate ? 'trip.startDate' : 'trip.startDateExpired';
            break;
          case 'pet':
            insuranceDataKey = 'proposer.dateOfInsuranceStart';
            break;
          default:
            insuranceDataKey = 'cover.dateOfInsuranceStart';
        }

        const dateOfInsuranceStart = get(formData.value, insuranceDataKey);
        const isDateBefore = isDateBeforeTodayUtc(dateOfInsuranceStart);

        if (data.questionSetChanged) {
          return { questionSetChanged: data.questionSetChanged };
        }

        // Temporary condition for the car product with removed prevalidation
        const dateOfInsuranceStartExpired = productName === 'car' ? isDateBefore || !dateOfInsuranceStart : isDateBefore;

        // Check question set version & quote expiration
        if (process.env.VUE_APP_PRODUCT_VERSION !== formData.value.productVersion) {
          modalAlert.value = MODAL_ALERTS.expiredQuestions;
        } else if (dateOfInsuranceStartExpired) {
          modalAlert.value = MODAL_ALERTS.quoteExpired;
        }

        if (data.autoquote) toggleAutoquoteModal(true);
      }
    } catch (error) {
      console.error(error.toString());
      console.error('Could not retrieve the latest progress on the form');
    }
  };

  const saveJourney = async (step) => {
    try {
      await root.$api.processJourney({
        productName: apiProductName,
        step,
        productPartnerId: config.partner,
        quoteData: formData.value[step],
      });
    } catch (error) {
      console.error(error.toString());
    }
  };

  /*
  * RESULTS
  */
  const updatePricing = (results) => {
    return results.value.map((quote) => {
      const details = quote.details[0];
      const initialAnnual = parseFloat(details.annualPremium);
      const initialDeposit = parseFloat(details.deposit);
      const initialMonthlyPremium = parseFloat(details.monthlyPremium);
      const initialMonthlyTotal = parseFloat(details.monthlyTotal);

      let annual = initialAnnual;
      let monthlyTotal = initialMonthlyTotal;
      let deposit = initialDeposit;
      let monthly = initialMonthlyPremium;

      let numberOfPayments = details.numberOfInstallments;
      let variedAmount = parseFloat(details.variedAmount);

      if (optionalExtras.value.length && productName !== 'breakdown') {
        if (variedAmount) {
          numberOfPayments += 1;
        }

        optionalExtras.value.forEach((extra) => {
          const extraCost = parseFloat(details?.optionalExtra[extra]?.cost?.value);

          // The interest rate should be calculated using monthly and annual values with no extras
          const interestRate = ((initialMonthlyTotal - initialAnnual) / initialAnnual + 1);
          let extraCostWithInterest = extraCost * round(interestRate, 2);

          // Locket or SoSure do not charge interest, temporary fix
          if (['Locket', 'SoSure'].includes(details.brokerFullName)) {
            extraCostWithInterest = extraCost;
          }

          if (extraCost > 0) {
            const monthlyExtra = (deposit / monthlyTotal) * extraCostWithInterest;

            annual += extraCost;
            deposit += monthlyExtra;
            monthly += (extraCostWithInterest - monthlyExtra) / numberOfPayments;
            monthlyTotal = round(deposit, 2) + (numberOfPayments * round(monthly, 2));

            if (variedAmount) {
              variedAmount = ((extraCostWithInterest - monthlyExtra) / numberOfPayments) + variedAmount;
            }
          }
        });
      }

      return {
        ...quote,
        details: [{
          ...quote.details[0],
          calcAnnual: parseFloat(annual),
          calcDeposit: parseFloat(deposit),
          calcMonthly: parseFloat(monthly),
          calcMonthlyTotal: parseFloat(monthlyTotal),
          calcVariedAmount: parseFloat(variedAmount),
        }],
      };
    });
  };

  const round = (num, places) => +(Math.round(num + `e+${places}`) + `e-${places}`);

  /*
  *  creates a value for optional extras
  */
  const updateTravelExtras = (extras) => {
    if (!extras) return;
    optionalExtras.value = ADDITIONAL_COVER_OPTIONS
      .filter(option => extras[option.trackerValue])
      .map(option => option.value);
  };

  const reset = async () => {
    if (request.value) {
      await request.value.cancel();
      request.value = null;
    }

    allResults.value = [];
    failedResults.value = [];
    isNoResults.value = false;
    quoteSummary.value = null;
    quotingDonePercentage.value = 0;
    quoteRef.value = null;
    isOptionsDrawerVisible.value = false;
    isDetailsDrawerOpen.value = false;
    modalAlert.value = false;
    hasSucceededQuotes.value = false;
  };

  const changeFilter = function ({ filter, value }, selectedFilters) {
    // handle change filters without requote
    this[filter] = value;
    restSelectedFilters.value = { ...selectedFilters, [filter]: value };

    root.$ls.set(productName + '__selected-options', restSelectedFilters.value);
    allResults.value = updatePricing(allResults);
    if (brokerResults.value.length) {
      brokerResults.value = updatePricing(brokerResults);
    }
  };

  const initSelectedFilters = function () {
    // get selected filters from storage
    // need to apply filters after page refresh
    const initialSelectedFilters = this.$ls.get(productName + '__selected-options');
    restSelectedFilters.value = initialSelectedFilters;

    if (initialSelectedFilters) {
      for (const filter in initialSelectedFilters) {
        this[filter] = initialSelectedFilters[filter];
      }

      allResults.value = updatePricing(allResults);
      if (brokerResults.value.length) {
        brokerResults.value = updatePricing(brokerResults);
      }
    }
  };

  /*
  * OTHER
  */
  const processCookies = async () => {
    try {
      const response = await root.$api.getJourneyCookiesToSet();
      const { status, data } = response;
      if (status === 'success') {
        Object.values(data).forEach(cookie => {
          if (cookie.expires.toString() === '-1') {
            Vue.$cookies.remove(cookie.name);
          } else {
            Vue.$cookies.set(
              cookie.name,
              cookie.value,
              (new Date(+`${cookie.expires}000`)).toUTCString(),
              cookie.path,
              cookie.domain,
            );
          }
        });
      }
    } catch (error) {
      if (error?.response?.data?.errors) {
        console.error(JSON.stringify(error.response.data.errors, null, 2));
      }
    }
  };

  const goToForm = (step = 1) => {
    root.$router.push({
      name: `${productName}-insurance`,
      query: {
        step,
      },
    }).catch(() => {});
  };

  const evaluateOptionalExtrasFilter = (value, filter) => {
    switch (filter.rule) {
      case 'above':
        return value >= filter.target;
      case 'below':
        return value <= filter.target;
      case 'exactly':
        return value === filter.target;
      default:
        return false;
    }
  };

  const productFilters = {
    common: (quote) => {
      const quoteTrackingType = quote.details[0]?.telematicsDetails?.trackingType;
      const quoteHasTrackingType = !!quoteTrackingType && typeof quoteTrackingType === 'string';
      const quoteMonthlyPaymentDetails = quote.details[0]?.monthlyPaymentDetails;

      // filter by telematics
      if (quoteHasTrackingType && !telematics.value.includes(quoteTrackingType.toLowerCase())) return false;

      // filter by policyTypes
      return !(policyTypes.value.length && !policyTypes.value.includes(quoteMonthlyPaymentDetails.text));
    },
    home: (quote) => {
      const quoteOptionalExtras = quote.details[0]?.optionalExtra;
      const insured = formData.value?.insured?.[0];

      // buildings damage
      const isBuildingsAccidentalDamageCovered = insured?.buildAccDam;
      const isQuoteBuildingsAccidentalDamageNotAvailable = quoteOptionalExtras?.['building/damage']?.included === 'N';
      if (isBuildingsAccidentalDamageCovered && isQuoteBuildingsAccidentalDamageNotAvailable) return false;

      // contents damage
      const isContentsAccidentalDamageCovered = insured?.contentsAccDam;
      const isQuoteContentsAccidentalDamageNotAvailable = quoteOptionalExtras?.['contents/damage']?.included === 'N';
      return !(isContentsAccidentalDamageCovered && isQuoteContentsAccidentalDamageNotAvailable);
    },
    travel: (quote) => {
      for (const dropdown of travelDropdowns.value) {
        let value;

        if (dropdown.type === 'maxExcess') {
          value = quote.details[0]?.maxExcess;
        } else {
          value = quote.details[0].optionalExtra[dropdown.type].cover;

          // The value we're filtering against doesn't exist
          if (value === null) {
            break;
          }
        }

        if (!evaluateOptionalExtrasFilter(value, dropdown.options.filter(option => option.value.key === dropdown.selected)[0].value)) {
          return false;
        }
      }

      return true;
    },
    pet: (quote) => {
      // coverType
      if (restSelectedFilters.value?.coverTypes?.toLowerCase() !== quote.details[0]?.coverType?.toLowerCase()) return false;

      // vetFees
      if (restSelectedFilters.value?.vetFees > +quote.details[0]?.optionalExtra.vetsFee.value) return false;

      return true;
    },
    breakdown: (quote) => {
      const quoteDetails = quote.details[0];
      const filterCoverType = restSelectedFilters.value?.coverType;
      const filterCoverLevel = restSelectedFilters.value?.coverLevel;
      const isQuoteCoverLevelLocal = quoteDetails.recoveryLocal;
      const isQuoteCoverLevelNational = quoteDetails.recoveryUk;
      const isQuoteCoverLevelEuropean = quoteDetails.european;
      const isCoverTypeVehicle = quoteDetails.coverType === 'Vehicle';

      // filter by coverType
      if (filterCoverType && filterCoverType === 'Vehicle' && !isCoverTypeVehicle) return false;
      if (filterCoverType && filterCoverType === 'Personal' && isCoverTypeVehicle) return false;

      // filter by coverLevel
      if (filterCoverLevel && filterCoverLevel === 'local' && !isQuoteCoverLevelLocal) return false;
      if (filterCoverLevel && filterCoverLevel === 'nationwide' && !isQuoteCoverLevelNational) return false;
      if (filterCoverLevel && filterCoverLevel === 'european' && !isQuoteCoverLevelEuropean) return false;

      return true;
    },
    bicycle: (quote) => {
      const quoteExcess = quote.details[0]?.optionalExtra?.excess?.compulsory?.value || null;
      const maxExcess = restSelectedFilters.value?.maxExcess;

      // filter by excess
      if (maxExcess === 'any') return true;
      if (Number(quoteExcess) > Number(maxExcess)) return false;

      return true;
    },
  };

  const checkSameExtras = (quoteExtras, quoteToCompareExtras) => {
    return Object.keys(quoteExtras).every((quoteExtra) => {
      // Extras are the same
      if (quoteExtras[quoteExtra]?.included === quoteToCompareExtras[quoteExtra]?.included) return true;
      // Only one of the extras is included
      if (quoteExtras[quoteExtra]?.included === 'N' || quoteToCompareExtras[quoteExtra]?.included === 'N') return false;
      // Both extras are included - 'Y' or selected 'extra'
      if (quoteExtras[quoteExtra]?.included === 'Y' || quoteToCompareExtras[quoteExtra]?.included === 'Y') return optionalExtras.value.includes(quoteExtra);

      return true;
    });
  };

  const hasAllSelectedExtrasIncluded = (quoteDetails) => {
    return !optionalExtras.value.some((extra) => {
      // (has different structure as other products
      if (productName === 'breakdown') {
        return !quoteDetails[extra];
      }

      return quoteDetails?.optionalExtra[extra]?.included === 'N';
    });
  };

  const isPriceDuplicatedForSameBroker = (results = allResults, quote) => {
    const quoteDetails = quote.details[0];
    const broker = quoteDetails.brokerFullName;

    // Check on all results if there is other quote from same broker with same price and same optional extras
    return results.value
      .some((quoteToCompare) => {
        const quoteDetailsToCompare = quoteToCompare.details[0];
        // Skip if it's from different broker
        if (quoteDetailsToCompare.brokerFullName !== broker) return false;
        // Skip if it's the same quote
        if (quoteDetailsToCompare.premiumID === quoteDetails.premiumID) return false;

        if (quoteDetailsToCompare.calcAnnual === quoteDetails.calcAnnual &&
          quoteDetailsToCompare.annualPremium > quoteDetails.annualPremium &&
          checkSameExtras(quoteDetailsToCompare?.optionalExtra, quoteDetails?.optionalExtra)
        ) {
          return true;
        }
      });
  };

  const filterShownResults = (results = allResults) => {
    return results.value
      .map((quote) => {
        // common filters (shared between products)
        if (!productFilters.common(quote)) return { ...quote, isVisible: false };

        // filters by product
        if (productName in productFilters) {
          if (!productFilters[productName](quote)) return { ...quote, isVisible: false };
        }

        // filter by optional extras
        if (!hasAllSelectedExtrasIncluded(quote.details[0])) return { ...quote, isVisible: false };

        // For bicycle do not show identical prices(with same optional extras) from the same broker
        if (productName === 'bicycle' && isPriceDuplicatedForSameBroker(results, quote)) return { ...quote, isVisible: false };

        return {
          ...quote,
          isVisible: true,
        };
      })
      .sort((a, b) => {
        let monthlySortingKey = 'calcMonthlyTotal';

        if (productName === 'pet') {
          monthlySortingKey = 'calcMonthly';
        }

        // if monthly payment type for quote 0 - this quote should be displayed at the bottom of results
        // in this case instead of monthly price we show 'Instalments Check with provider' message
        if (isViewMonthly.value) {
          if (!a.details[0][monthlySortingKey] &&
            !b.details[0][monthlySortingKey]) return 0;

          if (!a.details[0][monthlySortingKey]) return 1;

          if (!b.details[0][monthlySortingKey]) return -1;
        }

        const priceToUse = (quote) => (
          isViewMonthly.value ? quote.details[0][monthlySortingKey]
            : quote.details[0].calcAnnual
        );

        return priceToUse(a) - priceToUse(b);
      });
  };

  watch(isQuotesLoading, () => {
    if (!isQuotesLoading.value) {
      abtastyCampaigns();
    }
  });

  watch(isResultsRequoteShown, () => {
    if (isResultsRequoteShown) {
      WebFont.load({
        google: {
          families: [
            'Source Sans Pro:600',
          ],
        },
      });
    }
  },
  { immediate: true });

  const clickViewDetails = (event, index) => {
    detailsQuote.value = event;
    quotePositionNumber.value = index + 1;
  };

  // Lifecycle hooks
  onBeforeUnmount(() => {
    removeAbtastyScript(config);
  });

  const openMoreInfoOnEmailLink = () => {
    if (constants.PARTNERS.QuoteZone.includes(config.partner) && hasUrlQueryActionForShowDetails.value) {
      clickViewDetails(shownResults.value[0], 0);
      openDetailsDrawer(['results-details', 'optionalExtras']);
    }
  };

  return {
    productName,
    formData,
    isNoResults,
    allResults,
    failedResults,
    optionalExtras,
    policyTypes,
    telematics,
    isQuotesLoading,
    hasSucceededQuotes,
    isViewMonthly,
    restSelectedFilters,
    isUpdateQuoteShow,
    quoteSummary,
    detailsQuote,
    quotingDonePercentage,
    quoteRef,
    getDetailsDescription,
    getInitDetailsDescription,
    shownResults,
    modalAlert,
    getQuotes,
    brokerResults,
    brokerShownResults,
    resultsPageDisplay,
    resultsPageLayout,
    updatePricing,
    reQuote,
    cancelReQuote,
    toggleQuoteShown,
    goToForm,
    reset,
    changeFilter,
    initSelectedFilters,
    quotePositionNumber,
    insuredName,
    questionSetChanged,
    quotesQuantity,

    // other
    maxExcess,
    drawerDetails,
    isDetailsDrawerOpen,
    isOptionsDrawerVisible,
    drawerComponent,
    closeDetailsDrawer,
    drawerWidth,
    openDetailsDrawer,
    quoteVehicleDetailsMake,
    quoteCoverDetails,
    quoteVehicleDetailsUse,
    isAutoquteModalShown,
    toggleAutoquoteModal,
    selectedOptionalExtras,
    clickViewDetails,
    openMoreInfoOnEmailLink,
    isResultsRequoteShown,
    isResultsRequoteModalShown,
    resultsRequoteRedirectUrl,
  };
};
